import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Search, User, FileText, AlertCircle, Pill, ChevronRight, UserPlus, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function PatientSearch() {
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showNewPatient, setShowNewPatient] = useState(false);

  const fetchPatients = useCallback(async () => {
    setLoading(true);
    try {
      const params = search.length >= 2 ? `?search=${encodeURIComponent(search)}` : '';
      const res = await api.get(`/patients${params}`);
      setPatients(res.data.patients || []);
    } catch (err) {
      toast.error('Failed to load patients');
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    const timer = setTimeout(() => fetchPatients(), search ? 300 : 0);
    return () => clearTimeout(timer);
  }, [fetchPatients, search]);

  return (
    <div className="p-4 sm:p-6 max-w-4xl animate-fadeIn" data-testid="patient-search-page">
      {/* Header */}
      <div className="mb-5">
        <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Space Grotesk' }}>
          Patients
        </h1>
        <p className="text-sm text-muted-foreground mt-0.5">
          Search by name or MRN to open a patient's chart
        </p>
      </div>

      {/* Search + Add Patient */}
      <div className="flex gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-muted-foreground" />
          <Input
            className="pl-9 h-10 text-sm"
            placeholder="Search patients by name or MRN..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            autoFocus
            data-testid="patient-search-input"
          />
        </div>
        <Button onClick={() => setShowNewPatient(true)} className="h-10 gap-1.5" data-testid="add-patient-btn">
          <UserPlus className="w-4 h-4" /> Add Patient
        </Button>
      </div>

      {/* New Patient Modal */}
      {showNewPatient && (
        <AddPatientModal
          onCreated={(patient) => {
            setShowNewPatient(false);
            fetchPatients();
            const pid = patient.id || patient._id;
            if (pid) navigate(`/chart/${pid}`);
          }}
          onClose={() => setShowNewPatient(false)}
        />
      )}

      {/* Results */}
      {loading ? (
        <div className="space-y-2">
          {[1, 2, 3].map(i => <Skeleton key={i} className="h-16 w-full" />)}
        </div>
      ) : patients.length === 0 ? (
        <div className="text-center py-12">
          <User className="w-8 h-8 mx-auto mb-2 text-muted-foreground/30" />
          <p className="text-sm text-muted-foreground">
            {search ? 'No patients found' : 'Start typing to search'}
          </p>
        </div>
      ) : (
        <div className="space-y-1">
          <p className="text-xs text-muted-foreground mb-2">{patients.length} patient{patients.length !== 1 ? 's' : ''}</p>
          {patients.map(p => {
            const activeProblems = (p.problem_list || []).filter(x => x.status === 'active');
            const activeMeds = (p.medications || []).filter(x => x.status === 'active');
            const allergies = p.allergies || [];
            return (
              <Card
                key={p.id}
                className="cursor-pointer hover:shadow-md hover:border-primary/20 transition-all"
                onClick={() => navigate(`/chart/${p.id}`)}
                data-testid={`patient-card-${p.mrn}`}
              >
                <CardContent className="flex items-center gap-4 py-3 px-4">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-sm font-semibold text-primary shrink-0">
                    {p.name?.first?.[0]}{p.name?.last?.[0]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">{p.name?.first} {p.name?.last}</span>
                      <Badge variant="outline" className="text-[10px] font-mono tabular-nums">{p.mrn}</Badge>
                      {allergies.length > 0 && (
                        <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200 gap-0.5">
                          <AlertCircle className="w-2.5 h-2.5" /> {allergies.length} allerg{allergies.length > 1 ? 'ies' : 'y'}
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-0.5 text-[11px] text-muted-foreground">
                      <span className="tabular-nums">DOB: {p.dob}</span>
                      <span>{p.sex}</span>
                      {p.phone && <span>{p.phone}</span>}
                      {activeProblems.length > 0 && (
                        <span className="flex items-center gap-0.5">
                          <FileText className="w-3 h-3" /> {activeProblems.length} active problem{activeProblems.length > 1 ? 's' : ''}
                        </span>
                      )}
                      {activeMeds.length > 0 && (
                        <span className="flex items-center gap-0.5">
                          <Pill className="w-3 h-3" /> {activeMeds.length} med{activeMeds.length > 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}


function AddPatientModal({ onCreated, onClose }) {
  const [form, setForm] = useState({ first_name: '', last_name: '', dob: '', sex: 'M', phone: '', address: '', payer_name: '', member_id: '' });
  const [creating, setCreating] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleCreate = async () => {
    if (!form.first_name.trim() || !form.last_name.trim()) { toast.error('First and last name are required'); return; }
    if (!form.dob) { toast.error('Date of birth is required'); return; }
    setCreating(true);
    try {
      const res = await api.post('/patients', form);
      const patient = res.data.patient || res.data;
      toast.success(`Created chart for ${form.first_name} ${form.last_name}`);
      onCreated(patient);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create patient'); }
    finally { setCreating(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" data-testid="add-patient-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-[480px] overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
            <UserPlus className="w-4 h-4 text-blue-500" /> Add New Patient
          </h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-100"><X className="w-4 h-4 text-slate-400" /></button>
        </div>
        <div className="px-5 py-4 space-y-3">
          {/* Required fields */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] text-slate-500 uppercase font-semibold">First Name <span className="text-rose-500">*</span></label>
              <input value={form.first_name} onChange={e => set('first_name', e.target.value)}
                className="w-full mt-0.5 px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200"
                placeholder="First name" autoFocus data-testid="new-patient-first" />
            </div>
            <div>
              <label className="text-[10px] text-slate-500 uppercase font-semibold">Last Name <span className="text-rose-500">*</span></label>
              <input value={form.last_name} onChange={e => set('last_name', e.target.value)}
                className="w-full mt-0.5 px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200"
                placeholder="Last name" data-testid="new-patient-last" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] text-slate-500 uppercase font-semibold">Date of Birth <span className="text-rose-500">*</span></label>
              <input type="date" value={form.dob} onChange={e => set('dob', e.target.value)}
                className="w-full mt-0.5 px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200"
                data-testid="new-patient-dob" />
            </div>
            <div>
              <label className="text-[10px] text-slate-500 uppercase font-semibold">Sex</label>
              <select value={form.sex} onChange={e => set('sex', e.target.value)}
                className="w-full mt-0.5 px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200">
                <option value="M">Male</option>
                <option value="F">Female</option>
              </select>
            </div>
          </div>

          {/* Optional fields */}
          <div className="pt-2 border-t border-slate-100">
            <p className="text-[9px] text-slate-400 uppercase font-semibold mb-2">Optional</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] text-slate-500">Phone</label>
                <input value={form.phone} onChange={e => set('phone', e.target.value)}
                  className="w-full mt-0.5 px-3 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200"
                  placeholder="555-0100" />
              </div>
              <div>
                <label className="text-[10px] text-slate-500">Address</label>
                <input value={form.address} onChange={e => set('address', e.target.value)}
                  className="w-full mt-0.5 px-3 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200"
                  placeholder="123 Main St" />
              </div>
              <div>
                <label className="text-[10px] text-slate-500">Insurance / Payer</label>
                <input value={form.payer_name} onChange={e => set('payer_name', e.target.value)}
                  className="w-full mt-0.5 px-3 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200"
                  placeholder="Medicare, BCBS, etc." />
              </div>
              <div>
                <label className="text-[10px] text-slate-500">Member ID</label>
                <input value={form.member_id} onChange={e => set('member_id', e.target.value)}
                  className="w-full mt-0.5 px-3 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200"
                  placeholder="ID number" />
              </div>
            </div>
          </div>
        </div>
        <div className="px-5 py-3 border-t border-slate-100 bg-slate-50/50 flex items-center gap-2 justify-end">
          <Button variant="ghost" size="sm" className="text-xs" onClick={onClose}>Cancel</Button>
          <Button size="sm" className="text-xs gap-1 bg-blue-600 hover:bg-blue-700" onClick={handleCreate} disabled={creating}
            data-testid="create-patient-btn">
            {creating ? <Loader2 className="w-3 h-3 animate-spin" /> : <UserPlus className="w-3 h-3" />}
            Create Patient Chart
          </Button>
        </div>
      </div>
    </div>
  );
}
